import { Controller } from "@hotwired/stimulus"

// Attach to a form that does a full-page POST. After the first submit it
// blocks re-submission (double Enter, double click) and swaps the submit
// button for a spinner until the response navigates away.
//
// The browser builds the form data set AFTER submit handlers run, so named
// controls must not be `disabled` here or their values would be dropped from
// the request (e.g. the attachment file input). The re-submission guard is
// the `locked` flag; the visual state uses readonly and CSS classes only.
//
// Usage: data: { controller: "submit-lock", action: "submit->submit-lock#lock" }
// Optional: data-submit-lock-label-value="Enviando..."
export default class extends Controller {
  static values = { label: { type: String, default: "Enviando..." } }

  static SPINNER = `<svg class="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
      <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
    </svg>`

  connect() {
    this.locked = false
    // A back-navigation can restore the locked page from the bfcache.
    this.onPageshow = (event) => { if (event.persisted) this.unlock() }
    window.addEventListener("pageshow", this.onPageshow)
  }

  disconnect() {
    window.removeEventListener("pageshow", this.onPageshow)
  }

  lock(event) {
    if (this.locked) {
      event.preventDefault()
      return
    }
    this.locked = true

    // form.elements includes controls associated via the form="" attribute,
    // so buttons living outside the form tag (modal footers) are covered.
    for (const control of this.element.elements) {
      if (control.matches("textarea, input[type=text]")) control.readOnly = true
      if (control.matches("button, input[type=file]")) {
        control.classList.add("opacity-50", "pointer-events-none")
        control.setAttribute("aria-disabled", "true")
      }
      if (control.matches("button[type=submit], button:not([type])")) {
        control.dataset.originalHtml = control.innerHTML
        control.innerHTML = `<span class="inline-flex items-center gap-2">${this.constructor.SPINNER}${this.labelValue}</span>`
      }
    }
    this.element.setAttribute("aria-busy", "true")
  }

  unlock() {
    this.locked = false
    for (const control of this.element.elements) {
      if (control.matches("textarea, input[type=text]")) control.readOnly = false
      if (control.matches("button, input[type=file]")) {
        control.classList.remove("opacity-50", "pointer-events-none")
        control.removeAttribute("aria-disabled")
      }
      if (control.dataset?.originalHtml) {
        control.innerHTML = control.dataset.originalHtml
        delete control.dataset.originalHtml
      }
    }
    this.element.removeAttribute("aria-busy")
  }
}
